import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, FolderOpen, CheckSquare, Users, Shield } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_44f04ad6-a165-46a5-bc68-49188ad32b32/artifacts/hy9ywl5e_WhatsApp%20Image%202026-03-09%20at%2019.34.44.jpeg";

export default function Landing() {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container-main">
          <div className="flex h-16 md:h-40 items-center justify-between">
            <Link to="/" className="flex items-center gap-3" data-testid="landing-logo">
              <img 
                src={LOGO_URL} 
                alt="The Eleventh" 
                className="h-12 md:h-32 w-auto object-contain"
              />
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" className="text-sm" data-testid="login-link">
                  Sign In
                </Button>
              </Link>
              <Link to="/login">
                <Button className="btn-primary" data-testid="get-started-btn">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-52 pb-20 relative overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1705321963943-de94bb3f0dd3?crop=entropy&cs=srgb&fm=jpg&q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.1
          }}
        />
        <div className="container-main relative z-10">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-4 animate-fade-in">
              Interior Design Studio
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight mb-6 animate-slide-up">
              Design with
              <br />
              <span className="text-muted-foreground">Purpose & Precision</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-xl mb-8 animate-slide-up stagger-1">
              A complete project management platform for interior design studios. 
              Manage projects, tasks, and client communication—all in one place.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-slide-up stagger-2">
              <Link to="/login">
                <Button className="btn-primary h-12 px-8" data-testid="hero-get-started">
                  Get Started
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="outline" className="btn-outline h-12 px-8">
                  Sign In
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 border-t border-border">
        <div className="container-main">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-4">
              Features
            </p>
            <h2 className="text-3xl md:text-4xl font-light tracking-tight">
              Everything You Need
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="p-8 border border-border bg-card animate-slide-up">
              <div className="p-4 bg-primary/10 text-primary w-fit mb-6">
                <FolderOpen className="h-6 w-6" />
              </div>
              <h3 className="text-lg font-medium mb-3">Project Management</h3>
              <p className="text-sm text-muted-foreground">
                Organize projects by city, track status, and manage files all in one place.
              </p>
            </div>

            <div className="p-8 border border-border bg-card animate-slide-up stagger-1">
              <div className="p-4 bg-chart-2/10 text-chart-2 w-fit mb-6">
                <CheckSquare className="h-6 w-6" />
              </div>
              <h3 className="text-lg font-medium mb-3">Task Tracking</h3>
              <p className="text-sm text-muted-foreground">
                Create tasks, set priorities, assign team members, and track progress visually.
              </p>
            </div>

            <div className="p-8 border border-border bg-card animate-slide-up stagger-2">
              <div className="p-4 bg-chart-3/10 text-chart-3 w-fit mb-6">
                <Users className="h-6 w-6" />
              </div>
              <h3 className="text-lg font-medium mb-3">Client Portal</h3>
              <p className="text-sm text-muted-foreground">
                Give clients access to view their projects and uploaded plans securely.
              </p>
            </div>

            <div className="p-8 border border-border bg-card animate-slide-up stagger-3">
              <div className="p-4 bg-accent text-accent-foreground w-fit mb-6">
                <Shield className="h-6 w-6" />
              </div>
              <h3 className="text-lg font-medium mb-3">Role-Based Access</h3>
              <p className="text-sm text-muted-foreground">
                Control who can view, edit, upload, and delete with granular permissions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-muted">
        <div className="container-main text-center">
          <h2 className="text-3xl md:text-4xl font-light tracking-tight mb-4">
            Ready to Transform Your Workflow?
          </h2>
          <p className="text-muted-foreground mb-8 max-w-md mx-auto">
            Join The Eleventh and streamline your interior design projects today.
          </p>
          <Link to="/login">
            <Button className="btn-primary h-12 px-8" data-testid="cta-get-started">
              Get Started Now
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-8">
        <div className="container-main">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img 
                src={LOGO_URL} 
                alt="The Eleventh" 
                className="h-12 w-auto"
              />
              <span className="text-xs text-muted-foreground">
                © {new Date().getFullYear()} Interior Design Studio
              </span>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/privacy" className="text-xs text-muted-foreground hover:text-foreground transition-colors">
                Privacy
              </Link>
              <Link to="/terms" className="text-xs text-muted-foreground hover:text-foreground transition-colors">
                Terms
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
